// Per-connector-type save handlers extracted from saveConnectorFromEditor.
// Each returns either an upsert input ready for `upsertConnector`, or an
// error message. Display name is validated by the caller.

import {
  EMAIL_RECIPIENT_HARD_CAP,
  isValidEmail,
  normalizeRecipientList,
  type BeehiivConnectorConfig,
  type BentoConnectorConfig,
  type ButtondownConnectorConfig,
  type EmailConnectorConfig,
  type GhostConnectorConfig,
  type SubstackUnofficialConnectorConfig,
  type WebhookConnectorConfig,
  type WordPressConnectorConfig,
  type WordPressSelfHostedConnectorConfig,
} from "@/lib/composerCore";
import type { EditorState } from "@/components/composer/connectorManagerHelpers";

type Upsert = Parameters<typeof import("@/lib/composerCore").upsertConnector>[1];

export type HandlerResult =
  | { ok: true; upsert: Upsert; createMessage: string; updateMessage: string }
  | { ok: false; errorMessage: string };

export function buildWebhookUpsert(editor: EditorState): HandlerResult {
  if (!editor.targetUrl.trim()) return { ok: false, errorMessage: "Target URL is required" };
  const config: WebhookConnectorConfig = {
    target_url: editor.targetUrl.trim(),
    auth_header_name: editor.authHeaderName.trim() || null,
  };
  const secretValue = editor.clearSecret
    ? null
    : editor.secretValue.length > 0 ? editor.secretValue : undefined;
  return {
    ok: true,
    upsert: { connectorId: editor.connector?.id, connectorType: "webhook", displayName: editor.displayName.trim(), config, secretValue },
    createMessage: "Connector created",
    updateMessage: "Connector updated",
  };
}

export function buildKitUpsert(editor: EditorState): HandlerResult {
  const isCreate = !editor.connector;
  const trimmedSecret = editor.secretValue.trim();
  if (isCreate && !trimmedSecret) return { ok: false, errorMessage: "Kit API secret is required" };
  if (!isCreate && editor.clearSecret && !trimmedSecret) {
    return { ok: false, errorMessage: "Enter a new Kit API secret before clearing the stored one" };
  }
  const secretValue = editor.clearSecret
    ? null
    : trimmedSecret.length > 0 ? trimmedSecret : undefined;
  return {
    ok: true,
    upsert: {
      connectorId: editor.connector?.id, connectorType: "kit", displayName: editor.displayName.trim(),
      config: {} as WebhookConnectorConfig, secretValue,
    },
    createMessage: "Kit connector created",
    updateMessage: "Kit connector updated",
  };
}

export function buildBentoUpsert(editor: EditorState): HandlerResult {
  const isCreate = !editor.connector;
  const siteUuid = editor.bentoSiteUuid.trim();
  if (!siteUuid) return { ok: false, errorMessage: "Bento site UUID is required" };
  const publishable = editor.bentoPublishableKey.trim();
  const secret = editor.secretValue.trim();
  if (isCreate) {
    if (!publishable || !secret) return { ok: false, errorMessage: "Bento publishable key and secret key are both required" };
  } else {
    const provided = (publishable ? 1 : 0) + (secret ? 1 : 0);
    if (provided === 1) return { ok: false, errorMessage: "To rotate Bento credentials, provide both keys" };
  }
  const fromEmail = editor.bentoFromEmail.trim();
  if (fromEmail && !isValidEmail(fromEmail)) return { ok: false, errorMessage: "Bento from email is not a valid email" };
  const config: BentoConnectorConfig = {
    site_uuid: siteUuid,
    from_name: editor.bentoFromName.trim() || null,
    from_email: fromEmail || null,
  };
  const bothProvided = Boolean(publishable && secret);
  return {
    ok: true,
    upsert: {
      connectorId: editor.connector?.id, connectorType: "bento", displayName: editor.displayName.trim(),
      config,
      secretValue: bothProvided ? secret : undefined,
      publishableKey: bothProvided ? publishable : undefined,
    },
    createMessage: "Bento connector created",
    updateMessage: "Bento connector updated",
  };
}

export function buildWordPressUpsert(editor: EditorState, wpLinked: boolean | null): HandlerResult {
  if (wpLinked === false) {
    return { ok: false, errorMessage: "WordPress.com is not linked to this workspace. Link it in workspace settings first." };
  }
  const site = editor.wpSite.trim().replace(/^https?:\/\//i, "").replace(/\/+$/, "");
  if (!site) return { ok: false, errorMessage: "WordPress.com site is required" };
  const config: WordPressConnectorConfig = { site };
  return {
    ok: true,
    upsert: { connectorId: editor.connector?.id, connectorType: "wordpress", displayName: editor.displayName.trim(), config },
    createMessage: "WordPress connector created",
    updateMessage: "WordPress connector updated",
  };
}

export function buildWordPressSelfHostedUpsert(editor: EditorState): HandlerResult {
  const isCreate = !editor.connector;
  const siteUrl = editor.wpShSiteUrl.trim();
  const username = editor.wpShUsername.trim();
  if (!siteUrl) return { ok: false, errorMessage: "WordPress site URL is required" };
  if (!username) return { ok: false, errorMessage: "WordPress username is required" };
  const trimmedSecret = editor.secretValue.trim();
  if (isCreate && !trimmedSecret) return { ok: false, errorMessage: "WordPress Application Password is required" };
  if (trimmedSecret) {
    const cleaned = trimmedSecret.replace(/\s+/g, "");
    if (cleaned.length < 16) {
      return { ok: false, errorMessage: "Application Password must be at least 16 characters (spaces are ignored)" };
    }
  }
  const config: WordPressSelfHostedConnectorConfig = { site_url: siteUrl, username };
  return {
    ok: true,
    upsert: {
      connectorId: editor.connector?.id, connectorType: "wordpress_self_hosted",
      displayName: editor.displayName.trim(), config,
      secretValue: trimmedSecret.length > 0 ? trimmedSecret : undefined,
    },
    createMessage: "Self-hosted WordPress connector created",
    updateMessage: "Self-hosted WordPress connector updated",
  };
}

export function buildGhostUpsert(editor: EditorState): HandlerResult {
  const isCreate = !editor.connector;
  const adminUrl = editor.ghostAdminUrl.trim();
  if (!adminUrl) return { ok: false, errorMessage: "Ghost admin URL is required" };
  const trimmedSecret = editor.secretValue.trim();
  if (isCreate && !trimmedSecret) return { ok: false, errorMessage: "Ghost Admin API key is required" };
  if (trimmedSecret && !/^[a-f0-9]{24}:[a-f0-9]{64}$/i.test(trimmedSecret)) {
    return { ok: false, errorMessage: "Admin API key must be in the format `id:secret` (24 + 64 hex chars)" };
  }
  const config: GhostConnectorConfig = { admin_url: adminUrl };
  return {
    ok: true,
    upsert: {
      connectorId: editor.connector?.id, connectorType: "ghost",
      displayName: editor.displayName.trim(), config,
      secretValue: trimmedSecret.length > 0 ? trimmedSecret : undefined,
    },
    createMessage: "Ghost connector created",
    updateMessage: "Ghost connector updated",
  };
}

export function buildSubstackUnofficialUpsert(editor: EditorState): HandlerResult {
  const isCreate = !editor.connector;
  const publicationUrl = editor.substackPublicationUrl.trim().replace(/\/+$/, "");
  if (!publicationUrl) return { ok: false, errorMessage: "Substack publication URL is required" };
  if (!/^https:\/\//i.test(publicationUrl)) {
    return { ok: false, errorMessage: "Publication URL must start with https://" };
  }
  const trimmedSecret = editor.secretValue.trim();
  if (isCreate && !trimmedSecret) return { ok: false, errorMessage: "Substack session cookie is required" };
  const config: SubstackUnofficialConnectorConfig = { publication_url: publicationUrl };
  return {
    ok: true,
    upsert: {
      connectorId: editor.connector?.id, connectorType: "substack_unofficial",
      displayName: editor.displayName.trim(), config,
      secretValue: trimmedSecret.length > 0 ? trimmedSecret : undefined,
    },
    createMessage: "Substack connector created",
    updateMessage: "Substack connector updated",
  };
}

export function buildBeehiivUpsert(editor: EditorState): HandlerResult {
  const isCreate = !editor.connector;
  const pubId = editor.beehiivPublicationId.trim();
  if (!pubId) return { ok: false, errorMessage: "Beehiiv publication ID is required" };
  if (!/^pub_[A-Za-z0-9]{4,}$/.test(pubId)) {
    return { ok: false, errorMessage: "Publication ID must look like `pub_xxxxxxxx`" };
  }
  const trimmedSecret = editor.secretValue.trim();
  if (isCreate && !trimmedSecret) {
    return { ok: false, errorMessage: "Beehiiv API key is required" };
  }
  if (!isCreate && editor.clearSecret && !trimmedSecret) {
    return { ok: false, errorMessage: "Enter a new Beehiiv API key before clearing the stored one" };
  }
  const secretValue = editor.clearSecret
    ? null
    : trimmedSecret.length > 0 ? trimmedSecret : undefined;
  const config: BeehiivConnectorConfig = { publication_id: pubId };
  return {
    ok: true,
    upsert: {
      connectorId: editor.connector?.id, connectorType: "beehiiv",
      displayName: editor.displayName.trim(), config, secretValue,
    },
    createMessage: "Beehiiv connector created",
    updateMessage: "Beehiiv connector updated",
  };
}

export function buildButtondownUpsert(editor: EditorState): HandlerResult {
  const isCreate = !editor.connector;
  const trimmedSecret = editor.secretValue.trim();
  if (isCreate && !trimmedSecret) {
    return { ok: false, errorMessage: "Buttondown API key is required" };
  }
  if (!isCreate && editor.clearSecret && !trimmedSecret) {
    return { ok: false, errorMessage: "Enter a new Buttondown API key before clearing the stored one" };
  }
  const secretValue = editor.clearSecret
    ? null
    : trimmedSecret.length > 0 ? trimmedSecret : undefined;
  const config: ButtondownConnectorConfig = {} as ButtondownConnectorConfig;
  return {
    ok: true,
    upsert: {
      connectorId: editor.connector?.id, connectorType: "buttondown",
      displayName: editor.displayName.trim(), config, secretValue,
    },
    createMessage: "Buttondown connector created",
    updateMessage: "Buttondown connector updated",
  };
}




// buildBlueskyUpsert / buildMastodonUpsert removed 2026-07-03.

export function buildEmailUpsert(editor: EditorState): HandlerResult {
  const recipients = normalizeRecipientList(editor.recipients);
  if (recipients.length === 0) return { ok: false, errorMessage: "Add at least one recipient" };
  if (recipients.length > EMAIL_RECIPIENT_HARD_CAP) {
    return { ok: false, errorMessage: `Maximum ${EMAIL_RECIPIENT_HARD_CAP} recipients per connector` };
  }
  const replyTo = editor.replyTo.trim();
  if (replyTo && !isValidEmail(replyTo)) return { ok: false, errorMessage: "Reply-to is not a valid email" };
  const subjectTemplate = editor.subjectTemplate.trim() || "{{title}}";
  const config: EmailConnectorConfig = {
    from_name: editor.fromName.trim() || null,
    reply_to: replyTo || null,
    recipients,
    subject_template: subjectTemplate,
  };
  return {
    ok: true,
    upsert: { connectorId: editor.connector?.id, connectorType: "email", displayName: editor.displayName.trim(), config },
    createMessage: "Connector created",
    updateMessage: "Connector updated",
  };
}
